import { Box, Card, CardContent, Chip, Stack, Tooltip } from '@mui/joy';
import { Trash, Warning } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { ColoredChipList, TextLine, UserName } from 'core/elements';
import { formatDate, removeBrFromStartOrEnd } from 'core/utils';
import { theme } from 'theme';
import { Color, Direction, Size, TooltipPosition, Variant } from 'theme/enums';

import { EditNoteButton } from './EditNote';
import { ActionsWrapper, inlineButtonStyle } from './style';
import { IManagedNote, INoteProps } from './types';

interface IProps extends INoteProps {
  note: IManagedNote;
}

export const Note = ({
  note,
  onSubmitEditNote = null,
  onDeleteNote = null,
  textEllipsis = false,
  displayCard = true,
  displayActions = true,
}: IProps) => {
  const { t } = useTranslation();

  const partNoteContent = (
    <>
      {note.category && !note.category.inline && (
        <TextLine
          label={t('labelCategory')}
          text={note.category.label}
          bold
          endDecorator={
            note.category.infoTooltip ? (
              // This decorator renders inside TextLine's <p>, so its child must stay inline — a
              // styled Box wrapper (as core/elements/Warning uses) would nest a <div> in it.
              <Tooltip
                color={Color.WARNING}
                placement={TooltipPosition.TOP}
                title={note.category.infoTooltip}
              >
                {/* phosphor's `color` prop emits a fill attribute, which the global
                    `svg { fill: currentColor }` in index.css outranks — so set CSS
                    `color` here and let that rule resolve it. */}
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', color: theme.palette.warning.solidBg }}
                >
                  <Warning />
                </Box>
              </Tooltip>
            ) : undefined
          }
        />
      )}

      <TextLine
        text={removeBrFromStartOrEnd(note.content)}
        label={
          note.category?.inline && (
            <Chip color={note.category.color} size={Size.SM}>
              {`${note.category.label}:`}
            </Chip>
          )
        }
        textEllipsis={textEllipsis}
      />

      <Stack>
        <Stack direction={Direction.ROW} spacing={1}>
          {note.updatedAt && (
            <TextLine text={formatDate(note.updatedAt, true)} sx={{ fontWeight: 400 }} isSubText />
          )}

          <UserName
            label={t('labelBy')}
            userId={note.userId}
            firstName={note.firstName}
            lastName={note.lastName}
          />
        </Stack>

        {note.tags?.length > 0 && (
          <ColoredChipList values={note.tags} size={Size.SM} sx={{ fontWeight: 600 }} />
        )}
      </Stack>

      {displayActions && (
        <ActionsWrapper>
          {note.isEditable && <EditNoteButton note={note} onSubmit={onSubmitEditNote} level={1} />}

          {note.isDeletable && (
            <Button
              text={t('buttonLabelDelete')}
              color={Color.DANGER}
              variant={Variant.PLAIN}
              startDecorator={<Trash />}
              onClick={() => onDeleteNote(note)}
              sx={inlineButtonStyle}
            />
          )}
        </ActionsWrapper>
      )}
    </>
  );

  if (!displayCard) {
    return <Box>{partNoteContent}</Box>;
  }

  return (
    <Card color={Color.NEUTRAL} variant={Variant.PLAIN} sx={{ padding: '16px', ...note.sx }}>
      <CardContent>{partNoteContent}</CardContent>
    </Card>
  );
};
